import { Component, OnInit } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { Subscription } from "rxjs";
import { DeliveryLinkedMovementDbDao } from "../../../core/dao/db/deliveryLinkedMovement.db.dao";
import { DeliveryDto } from "../../../core/dto/delivery.dto";
import { DeliveryLinkedMovementDto } from "../../../core/dto/deliveryLinkedMovement.dto";
import { DeliveryService } from "../../../core/services/delivery.service";
import { UserService } from "../../../core/services/user.service";
import { DateHelper } from "../../../gyzmo-commons/helpers/date.helper";
import { DeviceHelper } from "../../../gyzmo-commons/helpers/device.helper";
import { LoadingHelper } from "../../../gyzmo-commons/helpers/loading.helper";
import { NavControllerExtended } from "../../../gyzmo-commons/helpers/navControllerExtended";
import { NetworkHelper } from "../../../gyzmo-commons/helpers/network.helper";
import { DateProvider } from "../../../gyzmo-commons/interfaces/dateProvider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { ListBasePage } from "../../list.base.page";

@Component({
    selector: "page-delivery",
    templateUrl: "delivery-list.page.html",
    styleUrls: ["delivery-list.page.scss"],
})
export class DeliveryListPage extends ListBasePage implements OnInit {
    deliveries: DeliveryDto[] = [];
    myDeliveries: DeliveryDto[] = [];
    otherDeliveries: DeliveryDto[] = [];

    cardFields: string[];

    mineShown: boolean = true;
    otherShown: boolean = false;

    synchronisationFinishedSubscription: Subscription;
    synchronized = false;
    protected readonly Number = Number;

    constructor(navCtrl: NavControllerExtended,
                logger: LoggerService,
                deviceHelper: DeviceHelper,
                dateProvider: DateProvider,
                networkHelper: NetworkHelper,
                userService: UserService,
                alertCtrl: AlertController,
                translateService: TranslateService,
                loadingHelper: LoadingHelper,
                private deliveryService: DeliveryService,
                private linkedMovementDbDao: DeliveryLinkedMovementDbDao) {
        super(navCtrl,
            logger,
            deviceHelper,
            dateProvider,
            networkHelper,
            userService,
            alertCtrl,
            translateService,
            loadingHelper);
    }

    ngOnInit() {
        this.cardFields = "$START_HOUR;$PLANNED_RETURN_HOUR;$DRIVER_NAME;;$EQUIPMENT_WORDING;$REGISTRATION_NUMBER".split(";");
    }

    async ionViewDidEnter() {
        await super.ionViewDidEnter();

        this.synchronisationFinishedSubscription = this.deliveryService.getSynchronisationFinishedObservable()
            .subscribe((synchronisationResult) => {
                this.filterDeliveries(synchronisationResult.day, synchronisationResult.orders);
                this.synchronized = true;
            });

        await this.refresh();
    }

    public onPageChanged(page: number): Promise<void> {
        this.operationsCount = [];

        this.fillDaysNamesAndDates(page);

        const startDate = this.getDay(this.daysIndexes[0], page).startOf("day");
        const endDate = this.getDay(this.daysIndexes[this.daysIndexes.length - 1], page).endOf("day");

        return this.deliveryService.getCountBetweenDates(startDate, endDate, this.user.thirdParty.id)
            .then((values) => {
                this.operationsCount = values;
            })
            .catch(reason => {
                this.logger.error(this.constructor.name, reason);
            });
    }

    public refreshDayInspection() {
        this.synchronized = false;

        const day = this.getDay(this.daysIndexes[this.dayIndex], this.page);

        return this.deliveryService.getListByDate(day)
            .then((values) => {
                this.filterDeliveries(day, values);
            })
            .catch(reason => {
                this.logger.error(this.constructor.name, reason);
            });
    }

    ionViewWillLeave() {
        this.synchronisationFinishedSubscription.unsubscribe();
    }

    filterDeliveries(listDay: DateTime, values: DeliveryDto[]) {
        // Vérifier le jour au cas où l'utilisateur aurait changé de jour avant la fin de synchronisation
        const actualDay = this.getDay(this.daysIndexes[this.dayIndex], this.page);
        if (!DateHelper.isSameDay(listDay, actualDay)) {
            return;
        }

        this.deliveries = values;
        this.myDeliveries = this.deliveries.filter(delivery => {
            return delivery.driver.id == this.user.thirdParty.id;
        });
        this.otherDeliveries = this.deliveries.filter(delivery => {
            return delivery.driver.id != this.user.thirdParty.id;
        });
    }

    public async openDetails(delivery: DeliveryDto) {
        await this.loadingHelper.showLoading();

        await this.deliveryService.getLinkedMovements(delivery)
            .then(async linkedMovements => {
                await this.deliveryService.getByIdOffline(delivery.id)
                    .then(async detailedDeliveryDto => {
                        if (!this.compareLinkedMovements(linkedMovements, delivery.linkedMovements)) {
                            delivery.linkedMovements = linkedMovements;

                            for (const linkedMovement of delivery.linkedMovements) {
                                await this.linkedMovementDbDao.save(linkedMovement.toModel());
                            }
                        }

                        await this.navCtrl.navigateForward("/delivery-details", { delivery: detailedDeliveryDto });
                        await this.loadingHelper.hideAll();
                    });
            });
    }

    private compareLinkedMovements(linkedMovements: DeliveryLinkedMovementDto[], linkedMovements2: DeliveryLinkedMovementDto[]) {
        if (linkedMovements.length != linkedMovements2.length) {
            return false;
        }

        linkedMovements = linkedMovements.sort(DeliveryLinkedMovementDto.sorter);
        linkedMovements2 = linkedMovements2.sort(DeliveryLinkedMovementDto.sorter);

        for (let i = 0; i < linkedMovements.length; i++) {
            if (!linkedMovements[i].equals(linkedMovements2[i])) {
                return false;
            }
        }

        return true;
    }
}
